import type { Socket } from 'socket.io-client';

// =============================================================================
// notification-socket.ts — Connecteur Socket.IO ISOLÉ du centre de notifications (R15-COM-06, COM-01).
// =============================================================================
// Transport temps réel (DEC-31) : path `/api/socket.io/`, namespace `/rt`, room `siret:<siret>` (auto-join
// SERVEUR à la connexion authentifiée — le client ne fabrique JAMAIS le siret ni ne rejoint de room lui-même,
// RLS + session cookie font autorité). Le worker émet `notification:new` avec un payload MINCE
// `{ kind:'notification', category:string }` : AUCUN contenu, AUCUNE PII. À réception, l'appelant INVALIDE
// les queries (refetch REST) et affiche un bandeau SILENCIEUX — le payload n'est JAMAIS rendu comme contenu.
//
// IMPORT DYNAMIQUE : `socket.io-client` est chargé à la demande (`await import(...)`) — le module n'est pas
// évalué au chargement de la SPA, et la connexion est isolée derrière ce seam (aucun couplage statique).
// Si le module ou la connexion échoue, on n'introduit AUCUN crash : le repli (polling REST) est porté par
// l'appelant (use-notification-realtime).

/** Payload MINCE du canal `notification:new` (COM-01). Aucun contenu, aucune PII : seule la catégorie. */
interface NotificationNewPayload {
  readonly kind: 'notification';
  readonly category: string;
}

export interface NotificationSocketHandlers {
  /** Appelé à chaque signal `notification:new` (catégorie MINCE, pour le bandeau générique uniquement). */
  readonly onSignal: (category: string) => void;
}

/**
 * Établit la connexion Socket.IO et abonne `notification:new`. Renvoie une fonction de DÉCONNEXION
 * synchrone : elle annule un chargement en cours (import non résolu) et ferme le socket une fois ouvert.
 * Ne lève jamais : les erreurs de chargement/connexion sont inertes (le repli polling reste actif côté hook).
 */
export function connectNotificationSocket(
  handlers: NotificationSocketHandlers,
): () => void {
  let cancelled = false;
  let socket: Socket | undefined;

  void (async (): Promise<void> => {
    try {
      const { io } = await import('socket.io-client');
      if (cancelled) {
        return;
      }
      // namespace `/rt`, path `/api/socket.io/`, cookies de session (auto-join room `siret:<siret>` serveur).
      socket = io('/rt', {
        path: '/api/socket.io/',
        withCredentials: true,
        transports: ['websocket', 'polling'],
      });
      socket.on('notification:new', (payload: NotificationNewPayload): void => {
        // On ne lit QUE la catégorie (payload mince) ; jamais affichée comme contenu (bandeau générique).
        handlers.onSignal(payload.category);
      });
    } catch {
      // Chargement/connexion impossible : inerte. Le repli polling REST (hook) couvre la fraîcheur.
    }
  })();

  return (): void => {
    cancelled = true;
    if (socket !== undefined) {
      socket.off('notification:new');
      socket.disconnect();
      socket = undefined;
    }
  };
}
